"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { type BlogPost } from "../../(global)/blog/data/blog/types";
import { slugify } from "@/utils/slugify";
import { cn } from "@/utils/cn";

interface RelatedArticlesProps {
  initialPosts: BlogPost[];
  keywords: string[];
  currentPostId?: number;
  type?: string;
  noPaddingX?: boolean;
}

export function RelatedArticles({
  initialPosts,
  keywords,
  currentPostId,
  type,
  noPaddingX,
}: RelatedArticlesProps) {
  const [posts, setPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    // Trier par date de création (du plus récent au plus ancien)
    const sortedPosts = initialPosts.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    // Chercher d'abord les articles liés
    const relatedPosts = sortedPosts
      .filter(
        (post) =>
          (!currentPostId || post.id !== currentPostId) &&
          keywords.some(
            (keyword) =>
              post.titre.toLowerCase().includes(keyword.toLowerCase()) ||
              post.sousTitre.toLowerCase().includes(keyword.toLowerCase()) ||
              post.labels.some((label) =>
                label.toLowerCase().includes(keyword.toLowerCase()),
              ),
          ),
      )
      .slice(0, 3);

    // Si pas assez d'articles liés, compléter avec les plus récents
    if (relatedPosts.length < 3) {
      const recentPosts = sortedPosts
        .filter(
          (post) =>
            !currentPostId ||
            (post.id !== currentPostId &&
              !relatedPosts.find((p) => p.id === post.id)),
        )
        .slice(0, 3 - relatedPosts.length);

      setPosts([...relatedPosts, ...recentPosts]);
    } else {
      setPosts(relatedPosts);
    }
  }, [currentPostId, keywords, initialPosts]);

  if (posts.length === 0) return null;

  const sectionTitle = {
    blog: "Articles similaires",
    salon: "Découvrez nos derniers articles",
    prestation: "Articles liés à cette prestation",
  }[type as string];

  return (
    <section
      className={cn(
        "w-full py-20",
        !noPaddingX && "px-4 ph:px-24 md:px-48 lg:px-72 xl:px-96",
      )}
    >
      <div className="flex flex-col gap-12">
        <h2 className="font-cormorant text-4xl ph:text-5xl sm:text-6xl lg:text-7xl">
          {sectionTitle}
        </h2>
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((post) => {
            const slug = `${slugify(post.titre)}-${slugify(post.sousTitre)}`;
            return (
              <Link
                key={post.id}
                href={`/blog/${slug}`}
                className="group flex flex-col"
              >
                <div className="relative aspect-video w-full overflow-hidden">
                  <Image
                    src={post.images[0]}
                    alt={post.titre}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    className="object-cover transition-transform duration-300 group-hover:grayscale"
                  />
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium group-hover:text-gold">
                    {post.titre}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm text-gray-600">
                    {post.sousTitre}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {post.labels.map((label, index) => (
                      <span key={index} className="text-xs text-gray-500">
                        #{label}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
